import { Box, useTheme } from "@mui/material";
import { type MRT_ColumnDef } from "material-react-table";
import React, { useMemo, useState } from "react";

import InviteManagementTableTopToolbar from "./InviteManagementTableTopToolbar";

import { AdminGlobalTable } from "../../../tables/AdminGlobalTable";
import { SessionActions } from "./SessionAction.cell";
import InviteUserTable from "../InviteUserTab/InviteUserTable";
import CreateSessionModal from "./modals/CreateSessionModal";
import { useGetSessions } from "../../../../tanstackQuery/queries/superAdmin/session";
import { useKeycloak } from "@react-keycloak/web";
import { useParams } from "react-router-dom";
import Loader from "../../common/Loader";
import { useQueryClient } from "@tanstack/react-query";
import { flattenCohorts } from "../../../../stores/jotai/base";
import { useGetOrgTreeData } from "../../../../tanstackQuery/queries/org-tree";

type InviteUser = {
  name: string;
  email: string;
  status: string;
  group: string;
  action: string;
};

type invoiceManagement = {
  cohort: string;
  session: string;
  status: string;
  created: string;
  users: InviteUser[];
};

const invoiceManagementCol = (
  setShowInviteTable: React.Dispatch<React.SetStateAction<boolean>>,
  setSelectedRowData: React.Dispatch<React.SetStateAction<invoiceManagement | null>>,
  cohortList
): MRT_ColumnDef<invoiceManagement>[] => [

  { header: "Session Name", accessorKey: "name" },
  {
    header: "Cohort Name",
    accessorKey: "groupID",
    Cell: ({ row }) => {
      const matched = cohortList.find(
        (cohort) => cohort?.groupID === row?.original?.groupID
      );
      return matched?.name;
    }
  },
  { header: "Status", accessorKey: "status" },
  { header: "Created", accessorKey: "created" },
  {
      id: "action",
      header: "Action",
      size: 10,
      Cell: (props) => SessionActions({
          ...props,
          row: props?.row?.original,
          setShowInviteTable,
          setSelectedRowData
      })
   },
];

const InviteManagementTable = () => {
  const client = useQueryClient();
  const { keycloak } = useKeycloak();
  const { orgID } = useParams();

  const [showInviteTable, setShowInviteTable] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [openSessionModal,setOpenSessionModal] = useState(false);

  const cachedOrgTreeData  = client.getQueryData(["orgtree",{ orgID }]) as any;

  const { data: orgTreeData, isLoading: isOrgTreeLoading } = useGetOrgTreeData(
      { accessToken: keycloak?.token },
      { orgID },
      {
          enabled: !!(cachedOrgTreeData && orgID && keycloak?.token),
          initialData: cachedOrgTreeData,
      }
  );

  const cohortList =  flattenCohorts(orgTreeData?.hierarchy) ;

  const { data: sessions, isLoading: isSessonLoading } = useGetSessions(
    { accessToken: keycloak?.token },
    { orgID },
    { enabled: !!(keycloak?.token && orgID) }
  );

  const columns = useMemo<MRT_ColumnDef<invoiceManagement>[]>(
    () => invoiceManagementCol(setShowInviteTable,setSelectedRowData,cohortList),
    []
  );
  const theme = useTheme();
  
  if(isSessonLoading){
    return <Loader />
  }
  
  return (
    <Box>
      {showInviteTable && selectedRowData ? (
        <InviteUserTable
          showInviteTable={showInviteTable}
          setShowInviteTable={setShowInviteTable}
          selectedRowData={selectedRowData}
          setSelectedRowData={setSelectedRowData}
        />
      ) : (
        <AdminGlobalTable
          columns={columns}
          data={sessions}
          config={{
            enablePagination: true,
            enableRowVirtualization: false,
            enableColumnVirtualization: false,
            enableStickyHeader: true,
            muiSelectCheckboxProps: { color: "secondary" },
            muiTableBodyRowProps: () => ({
              hover: false,
              sx: {
                backgroundColor: "transparent",
                ":hover": { backgroundColor: theme?.custom?.hoverOptionColor },
              },
            }),
            muiTableContainerProps: {
              sx: {
                maxHeight: "calc(100vh - 590px)",
                minHeight: "calc(100vh - 590px)",
              },
            },
            // renderDetailPanel: ({ row }) => <CohortUsersTable row={row} />,
            renderTopToolbarCustomActions: ({ table }) => (
              <InviteManagementTableTopToolbar table={table} setOpenSessionModal={setOpenSessionModal}/>
            ),
          }}
        />
      )}
      <CreateSessionModal
        handleClose={() => setOpenSessionModal(!openSessionModal)}
        open={openSessionModal}
        cohortList={cohortList}
      />
    </Box>
  );
};

export default InviteManagementTable;
